import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import {
  RATING_LEVELS,
  REGIONS,
  YEARS,
  filterMovies,
  getCategory,
  getMoviesByCategory,
  sortMovies,
  type SortKey,
} from "@/utils/movie";

const PAGE_SIZE = 8;

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "update", label: "按更新时间" },
  { key: "popular", label: "按热度" },
  { key: "name", label: "按名称" },
];

interface FilterChipsProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

function FilterChips({ label, options, value, onChange }: FilterChipsProps) {
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 text-sm text-foreground-400 pt-1.5 w-10">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              value === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { type } = useParams();
  const category = getCategory(type);

  usePageMeta({
    title: `激情丛林 - ${category.label}分类 - 高清影视免费在线观看`,
    description: category.description,
    keywords: `激情丛林,激情丛林${category.label},${category.label}在线观看,高清影视,在线播放`,
  });

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("全部");
  const [sort, setSort] = useState<SortKey>("update");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setRating("全部");
    setSort("update");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [region, year, rating, sort]);

  const result = useMemo(() => {
    const base = getMoviesByCategory(category);
    const filtered = filterMovies(base, { region, year, rating });
    return sortMovies(filtered, sort);
  }, [category, region, year, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const paged = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setRating("全部");
    setSort("update");
    setPage(1);
  };

  const hasActiveFilter =
    region !== "全部" || year !== "全部" || rating !== "全部" || sort !== "update";

  const renderFilters = () => (
    <div className="space-y-4">
      <FilterChips label="地域" options={REGIONS} value={region} onChange={setRegion} />
      <FilterChips label="年份" options={YEARS} value={year} onChange={setYear} />
      <FilterChips
        label="评分"
        options={RATING_LEVELS.map((item) => item.label)}
        value={rating}
        onChange={setRating}
      />
    </div>
  );

  return (
    <div className="px-4 md:px-6 py-6 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "分类" }, { label: category.label }]}
        />

        <div className="mt-5 mb-6 md:mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
            激情丛林 · {category.label}
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            {category.description}
          </p>
        </div>

        <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 mb-6 md:mb-8">
          <div className="hidden md:block">{renderFilters()}</div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="md:hidden flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 text-foreground-800 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line text-base" aria-hidden="true"></i>
              筛选条件
            </button>
            <span className="hidden md:block text-sm text-foreground-400">
              共 <span className="font-bold text-primary-700">{result.length}</span> 部影视内容
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <span className="hidden md:inline text-sm text-foreground-400">排序</span>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  className="appearance-none pl-3.5 pr-8 py-2 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 cursor-pointer outline-none focus:border-primary-400"
                  aria-label="排序方式"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <i
                  className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-lg"
                  aria-hidden="true"
                ></i>
              </div>
              {hasActiveFilter && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-accent-700 hover:bg-accent-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  重置
                </button>
              )}
            </div>
          </div>
        </div>

        {paged.length === 0 ? (
          <div className="py-20 text-center bg-background-50 rounded-lg border border-background-200">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
              <i className="ri-emotion-unhappy-line text-3xl" aria-hidden="true"></i>
            </span>
            <p className="text-foreground-700 font-semibold">未找到符合条件的影视内容</p>
            <p className="mt-1 text-sm text-foreground-400">试试调整筛选条件，或重置查看全部内容</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {paged.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 md:mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:border-primary-400 hover:enabled:text-primary-700 cursor-pointer transition-colors"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
            </button>
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index + 1}
                type="button"
                onClick={() => setPage(index + 1)}
                className={`min-w-9 h-9 px-3 flex items-center justify-center rounded-md text-sm font-semibold cursor-pointer transition-colors ${
                  page === index + 1
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:border-primary-400 hover:enabled:text-primary-700 cursor-pointer transition-colors"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </div>

      {filterOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 max-h-[80vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            {renderFilters()}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  setFilterOpen(false);
                }}
                className="flex-1 px-4 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}